import { useDragDropMonitor } from '@dnd-kit/react';
import { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChartDropPlace } from '../../model/chartDrop';
import type { PresetChartFormValues } from '../../model/dashboardPresetForm';
import { CHART_SIZE_COLUMNS, type ChartGridRow } from '../DashboardPresetGrid/chartLayout';
import {
  chartDropTarget,
  type ChartDropIndicatorShape,
  type ChartDropLayout,
  type ChartDropRow,
  type ChartDropTarget,
} from './chartDropTarget';

type Props = {
  charts: PresetChartFormValues[];
  /** Строки сетки по составу: по ним место понимается как «в эту строку» или «новой строкой» */
  rows: ChartGridRow[];
  /** Сетка дашборда — индикатор рисуется в её координатах */
  gridRef: RefObject<HTMLDivElement | null>;
  /** Диаграмма отпущена над показанным местом */
  onDrop: (chartId: string, place: ChartDropPlace) => void;
};

type Monitor = Parameters<typeof useDragDropMonitor>[0];
type DragManager = Parameters<NonNullable<Monitor['onDragMove']>>[1];

/** Расхождение в пикселях, начиная с которого колонки строки стоят уже не в одном ряду. */
const ROW_TOP_TOLERANCE = 1;

/** Отступ из вычисленного стиля; не измерившийся — нулевой. */
const pixels = (value: string): number => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Ширина индикатора; у вертикальной черты её нет. */
const shapeWidth = (shape: ChartDropIndicatorShape): number | null => ('width' in shape ? shape.width : null);

/** Высота индикатора; у горизонтальной черты её нет. */
const shapeHeight = (shape: ChartDropIndicatorShape): number | null => ('height' in shape ? shape.height : null);

const isSameShape = (first: ChartDropIndicatorShape | null, second: ChartDropIndicatorShape | null): boolean => {
  if (!first || !second) return first === second;
  return (
    first.kind === second.kind &&
    first.left === second.left &&
    first.top === second.top &&
    shapeWidth(first) === shapeWidth(second) &&
    shapeHeight(first) === shapeHeight(second)
  );
};

/**
 * Раскладка сетки на сейчас: ячейки диаграмм берутся с их мест на странице.
 *
 * Строка состава, колонки которой стоят на разной высоте, разбита на ряды, как её видит пользователь:
 * на узком экране колонки строки идут одна под другой, и каждая — свой ряд без пустого места.
 */
const measureLayout = (
  manager: DragManager,
  grid: HTMLDivElement,
  charts: PresetChartFormValues[],
  rows: ChartGridRow[],
  sourceId: string,
): ChartDropLayout | null => {
  const elementOf = (chartId: string) => manager.registry.draggables.get(chartId)?.element;
  const measure = (chartId: string) => elementOf(chartId)?.getBoundingClientRect();

  const sourceChart = charts.find((chart) => chart.chartId === sourceId);
  const sourceElement = elementOf(sourceId);
  if (!sourceChart || !sourceElement) return null;
  // Зазор между диаграммами складывается из отступов самих ячеек — их и берём с ячейки
  const sourceStyle = getComputedStyle(sourceElement);

  const dropRows: ChartDropRow[] = [];
  for (const row of rows) {
    let current: ChartDropRow | null = null;
    for (const column of row.columns) {
      const cells = column.chartIndexes.flatMap((index) => {
        const chartId = charts[index].chartId;
        const rect = measure(chartId);
        return rect ? [{ chartId, rect }] : [];
      });
      if (cells.length === 0) continue;
      // Колонки одного ряда начинаются на одной высоте — по верхним диаграммам их и сравниваем
      const previousHead = current?.columns.at(-1)?.cells[0];
      if (!current || !previousHead || Math.abs(previousHead.rect.top - cells[0].rect.top) > ROW_TOP_TOLERANCE) {
        current = { columns: [], freeColumns: 0 };
        dropRows.push(current);
      }
      current.columns.push({ cells });
    }
    // Незанятый конец есть только у последнего ряда строки
    if (current) current.freeColumns = row.freeColumns;
  }

  return {
    gridRect: grid.getBoundingClientRect(),
    rows: dropRows,
    source: {
      chartId: sourceId,
      rect: sourceElement.getBoundingClientRect(),
      columns: CHART_SIZE_COLUMNS[sourceChart.size],
    },
    cellGutter: { x: pixels(sourceStyle.paddingLeft), y: pixels(sourceStyle.marginBottom) },
  };
};

/**
 * Место перетаскиваемой диаграммы, показанное до отпускания: вертикальная черта между колонками
 * строки, горизонтальная — между строками, над первой строкой и между диаграммами колонки, контур
 * диаграммы — на пустом месте строки.
 *
 * Место ведётся одной подпиской на движение указателя, а не целями сброса у каждой ячейки: под
 * курсором его вычисляет одно правило, и отпускание применяет ровно последнее показанное место —
 * поэтому показанное и итог не расходятся. Сетка во время перетаскивания не перестраивается:
 * диаграммы стоят там, где стояли, и место отсчитывается от того, что видит пользователь.
 *
 * Поэтому и раскладка сетки снимается не на каждом движении, а один раз — в начале переноса, и
 * заново только после прокрутки, сдвинувшей диаграммы на странице: замер всех ячеек на каждом
 * движении заставлял браузер пересчитывать раскладку страницы, и на медленном компьютере место
 * отставало от курсора. Точка курсора берётся из самого события движения — положение переноса
 * обновляется уже после него, и место по нему отставало бы на движение.
 */
export const ChartDropIndicator: FC<Props> = ({ charts, rows, gridRef, onDrop }) => {
  const [indicator, setIndicator] = useState<ChartDropIndicatorShape | null>(null);
  // Последнее показанное место: его и применяет отпускание
  const targetRef = useRef<ChartDropTarget | null>(null);
  // Раскладка сетки на этот перенос; `null` — ещё не снята либо устарела прокруткой
  const layoutRef = useRef<ChartDropLayout | null>(null);

  const forgetLayout = useCallback(() => {
    layoutRef.current = null;
  }, []);

  // Прокрутку слушаем, только пока идёт перенос; ушедшая со страницы сетка подписку не оставляет
  const stopTracking = useCallback(() => {
    window.removeEventListener('scroll', forgetLayout, true);
    layoutRef.current = null;
  }, [forgetLayout]);
  useEffect(() => stopTracking, [stopTracking]);

  const handlers = useMemo<Monitor>(() => {
    const show = (target: ChartDropTarget | null) => {
      targetRef.current = target;
      const next = target?.indicator ?? null;
      setIndicator((previous) => (isSameShape(previous, next) ? previous : next));
    };

    return {
      onDragStart: () => {
        show(null);
        layoutRef.current = null;
        // Прокрутка любой области, включая страницу, сдвигает диаграммы относительно окна
        window.addEventListener('scroll', forgetLayout, true);
      },
      onDragMove: (event, manager) => {
        const { source, position } = manager.dragOperation;
        const grid = gridRef.current;
        const pointer =
          event.to ??
          (event.by ? { x: position.current.x + event.by.x, y: position.current.y + event.by.y } : position.current);
        if (!source || !grid || !pointer) {
          show(null);
          return;
        }
        layoutRef.current ??= measureLayout(manager, grid, charts, rows, String(source.id));
        show(layoutRef.current ? chartDropTarget(pointer, layoutRef.current) : null);
      },
      onDragEnd: (event) => {
        stopTracking();
        const target = targetRef.current;
        const sourceId = event.operation.source?.id;
        show(null);
        // Отпущенная вне сетки диаграмма остаётся на месте
        if (event.canceled || !target || sourceId === undefined) return;
        onDrop(String(sourceId), target.place);
      },
    };
  }, [charts, rows, gridRef, onDrop, forgetLayout, stopTracking]);
  useDragDropMonitor(handlers);

  if (!indicator) return null;

  if (indicator.kind === 'outline') {
    return (
      <div
        aria-hidden
        className="pointer-events-none absolute z-20 rounded-xl border-2 border-dashed border-primary bg-primary/10"
        style={{ left: indicator.left, top: indicator.top, width: indicator.width, height: indicator.height }}
      />
    );
  }

  if (indicator.kind === 'vertical') {
    return (
      <div
        aria-hidden
        className="pointer-events-none absolute z-20 w-1 -translate-x-1/2 rounded-full bg-primary"
        style={{ left: indicator.left, top: indicator.top, height: indicator.height }}
      />
    );
  }

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute z-20 h-1 -translate-y-1/2 rounded-full bg-primary"
      style={{ left: indicator.left, top: indicator.top, width: indicator.width }}
    />
  );
};
